/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable max-len */
import React, {
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { Tooltip } from '@jutro/components';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import metadata from './QuoteSummaryBarComponent.metadata.json5';
import styles from './QuoteSummaryBarComponent.module.scss';
import messages from './QuoteSummaryBarComponent.messages';
import PrivacyPolicyComponent from '../PrivacyPolicy/PrivacyPolicyComponent';

import { Link } from '@jutro/router';
import { MLNotificationBlockUtil } from 'ml-portals-util-react';


import { IntlContext } from '@jutro/legacy/locale';


function getContent(autoTermTypeList, quote, onUpdateTerm, translator) {
    const selectTermType = (selectedTerm) => {
        const currentTerm = _.get(quote, 'termType_ML.value', '');
        if (selectedTerm !== currentTerm) {
            onUpdateTerm(selectedTerm);
        }
    };

    // map the termType.code to a message
    return (
        <ul className={`styled-ul-list ${styles.changePlanTooltipUl}`}>
            {autoTermTypeList.map((termType) => {
                return (
                    <li key={termType.code}>
                        <Link onClick={() => selectTermType(termType)} className={styles.termItem}>
                            {translator(messages[termType.code])}
                        </Link>
                    </li>
                );
            })}
        </ul>
    );
}

function ChangePlanTooltipComponent(props) {
    ChangePlanTooltipComponent.propTypes = {
        autoTermTypeList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
        quote: PropTypes.shape({}).isRequired,
        onUpdateTerm: PropTypes.func.isRequired
    };
    const { autoTermTypeList, quote, onUpdateTerm } = props;

    const translator = useTranslator();

    return (
        <Tooltip
            id="tooltipPlanChange"
            placement="bottom"
            trigger="click"
            content={getContent(autoTermTypeList, quote, onUpdateTerm, translator)}
        >
            <a className={styles.changePlanTooltip}>
                {translator(messages.change)}
                <i className="icon-ml-carrot-down-outline" />
            </a>
        </Tooltip>
    );
}

function QuoteSummaryBarComponent(props) {
    const {
        quote,
        getFullPayPlan,
        runMainButton,
        recurringPlanPeriod,
        autoTermTypeList,
        onUpdateTerm,
        isBundle,
        onEmail,
        onPrint,
        mainButtonName,
        updateEffDate,
        isQuotingInProgress,
        isReadOnly,
        isPersonalAuto,
        isQnB,
        className: fromParent
    } = props;

    const translator = useTranslator();
    const selectedTermType = _.get(quote, 'termType_ML.value.code', '');
    const TERM_TYPE_HALF_YEAR = 'HalfYear';
    const TERM_TYPE_7_DAY = '7Days';
    const intl = useContext(IntlContext);
    const premiumInfo = !_.isUndefined(recurringPlanPeriod) ? recurringPlanPeriod.premium : quote.premium.value;
    const [fullPayPlan, setFullPayPlan] = useState({});
    const [showDiscounts, setShowDiscounts] = useState(false);
    const [dateEditable, setDateEditable] = useState(false);
    const [effDateLiveRegionMsg, setEffDateLiveRegionMsg] = useState('');
    const [hideHyperlink, setHideHyperLink] = useState(true);

    const currentEffectiveDate = new Date();

    const {
        clearBlockAlerts,
        addWarningAlert,
        addErrorAlert
    } = MLNotificationBlockUtil();


    useEffect(() => {
        setFullPayPlan(getFullPayPlan(quote));
    }, [getFullPayPlan, quote]);

    useEffect(()=>{
        if(isQnB){
            setHideHyperLink(false);
        }  
    },[isQnB]);

    const formatAmountString = useCallback((amount) => {
        try {
            return intl.formatNumber(amount.amount, { style: 'currency', currency: amount.currency });
        } catch (e) {
            return '';
        }
    }, [intl]);

    const getAnnualSavings = useCallback(() => {
        const fullPayPlanTotalAmount = _.get(fullPayPlan, 'premium.total.amount', undefined);
        try {
            const savings = premiumInfo.total.amount - fullPayPlanTotalAmount;
            const amount = {
                amount: savings > 0 ? savings : 0.00,
                currency: premiumInfo.total.currency
            };
            return formatAmountString(amount);
        } catch (e) {
            return '';
        }
    }, [fullPayPlan, premiumInfo, formatAmountString]);

    const getTermtypeLabel = useCallback(() => {
        let label = translator(messages.termTypeAnnual);
        if (selectedTermType === TERM_TYPE_HALF_YEAR) {
            label = translator(messages.termType6Month);
        } else if (selectedTermType === TERM_TYPE_7_DAY) {
            label = translator(messages.termType7day);
        }
        return label;
    }, [translator, selectedTermType]);

    const getPerTermLabel = useCallback(() => {
        let label = translator(messages.perTermAnnual);
        if (selectedTermType === TERM_TYPE_HALF_YEAR) {
            label = translator(messages.perTerm6Month);
        } else if (selectedTermType === TERM_TYPE_7_DAY) {
            label = translator(messages.perTerm7day);
        }
        return label;
    }, [translator, selectedTermType]);

    const getPremiumDisplay = useCallback((message, premium) => {
        return (
            <div>
                {formatAmountString(premium)}
                <span className={styles.premiumSpan}>
                    {translator(message)}
                </span>
            </div>
        );
    }, [formatAmountString, translator]);

    const discountsListOverride = useCallback(() => {
        const discounts = _.get(quote, 'discounts_ML.value', []);
        const listOverride = discounts.map((discount, index) => {
            return {
                [`discountListItem${index}`]: { visible: index <= 2 || showDiscounts, className: isQnB ? "wrapTextQB" : "" }
            };
        });

        return Object.assign({}, ...listOverride);
    }, [quote, showDiscounts]);

    const onToggleShowDiscounts = useCallback(() => {
        setShowDiscounts(!showDiscounts);
    }, [setShowDiscounts, showDiscounts]);

    const onToggleDateEditable = useCallback(() => {
        setEffDateLiveRegionMsg('Showing Effective Date Input to edit');
        setDateEditable(!dateEditable);
    }, [setDateEditable, dateEditable]);

    const getFormattedDateString = useCallback((gwDateVal) => {
        const date = LocalDateUtil.toMidnightDate(gwDateVal);
        return intl.formatDate(date, { year: 'numeric', month: '2-digit', day: '2-digit' });
    }, [intl]);

    const onUpdateDate = useCallback((val) => {
        _.set(quote, 'periodStartDate.value', val);
        updateEffDate(quote.value);
        setDateEditable(false);
    }, [quote, updateEffDate]);

    const showLoader = useCallback(() => {
        return !!quote?.value?.quoteStatus?.showLoading && quote?.value?.hasError !== true;
    }, [quote?.value?.hasError, quote?.value?.quoteStatus?.showLoading]);

    const getCurrentDateObj = useCallback(() => {
        const now = new Date();

        return {
            year: now.getFullYear(),
            month: now.getMonth(),
            day: now.getDate() + 1
        };
    }, []);

    const getMaxDateObj = () => {
        const maxDate = new Date();
        maxDate.setDate(maxDate.getDate() + 90);

        return {
            year: maxDate.getFullYear(),
            month: maxDate.getMonth(),
            day: maxDate.getDate()
        };
    }


    const isCompleteDate = (value) => value && value.day != null && value.month != null && value.year != null;


    const validateEffectiveDate = (val) => {
        const minDateObj = getCurrentDateObj();
        const maxDateObj = getMaxDateObj(); 

        if(val.year < minDateObj.year
            || (val.year === minDateObj.year && val.month < minDateObj.month)
            || (val.year === minDateObj.year 
                && val.month === minDateObj.month 
                && val.day < minDateObj.day)) 
        {
            return false; 
        }

        if(val.year > maxDateObj.year
            || (val.year === maxDateObj.year && val.month > maxDateObj.month)
            || (val.year === maxDateObj.year 
                && val.month === maxDateObj.month 
                && val.day > maxDateObj.day)) 
        {
            return false; 
        }


        return true;
    };

    const overrides = {
        termTypeDiv: {
            alignContent: "center",
            visible: isPersonalAuto
        },
        termTypeP: {
            content: getTermtypeLabel()
        },
        term7DaysDiv: {
            visible: selectedTermType === TERM_TYPE_7_DAY
        },
        term7DaysPremium: {
            content: getPremiumDisplay(messages.premium7day, _.get(fullPayPlan, 'premium.total', undefined))
        },
        non7DaysTermDiv: {
            visible: selectedTermType !== TERM_TYPE_7_DAY
        },
        non7daysPremium: {
            visible: !_.isUndefined(_.get(premiumInfo, 'monthlyPremium.amount', undefined)) && !quote?.value?.quoteStatus?.showLoading,
            content: getPremiumDisplay(messages.non7DaysMonthlyPremium, _.get(premiumInfo, 'monthlyPremium', undefined)),
            className: _.get(quote, 'value.quoteStatus.isStale', false) ? 'premium strike-through' : 'premium headerOne'
        },
        totalCostPremiumInfo: {
            visible: !_.isUndefined(premiumInfo),
            content: `Total cost: ${formatAmountString(_.get(premiumInfo, 'total', 0))} ${getPerTermLabel()}`,
            className: _.get(quote, 'value.quoteStatus.isStale', false) ? 'body-copy vertical-spacing-20 strike-through' : 'body-copy vertical-spacing-20'
        },
        throbberLoadingTotalCostPremium: {
            visible: showLoader()
        },
        annualSaving: {
            visible: (!_.isUndefined(premiumInfo) && !_.isUndefined(fullPayPlan)) && !isQnB,
            content: getAnnualSavings(),
            className: _.get(quote, 'value.quoteStatus.isStale', false) ? 'premium strike-through' : 'premium'
        },
        fullPayPlanTotalCost: {
            visible: !_.isUndefined(fullPayPlan) && !isQnB,
            content: !_.isUndefined(_.get(fullPayPlan, 'premium', undefined)) ? `Total cost: ${formatAmountString(_.get(fullPayPlan, 'premium.total', undefined))} ${getPerTermLabel()}` : '',
            className: _.get(quote, 'value.quoteStatus.isStale', false) ? 'body-copy vertical-spacing-5 strike-through' : 'body-copy vertical-spacing-5'
        },
        stateFeesComponent: {
            totalFees: _.get(quote, 'totalChanges_Ext.value', {}),
            itemizedFees: _.get(quote, 'feesCosts_Ext.value', []),
            displayAlternativeTemplate: true,
            visible: !isQnB
        },
        changeplantooltipcomponent: {
            autoTermTypeList: autoTermTypeList,
            quote: quote,
            onUpdateTerm: onUpdateTerm,
            visible: selectedTermType !== TERM_TYPE_7_DAY && !isQnB
        },
        totalDiscounts: {
            content: <FormattedMessage
                {...messages.discountsDisplay}
                values={{
                    discountsLabel: (<strong>{translator(messages.discountsLabel)}</strong>),
                    discounts: (formatAmountString(_.get(quote, 'discountSavings_ML.value', undefined)))
                }}
            />,
            className: _.get(quote, 'value.quoteStatus.isStale', false) ? 'body-copy vertical-spacing-30 discounts-copy strike-through' : 'body-copy vertical-spacing-30 discounts-copy'

        },
        orLines: {
            visible: !isQnB
        },
        payFullAmount: {
            visible: !isQnB
        },
        ...discountsListOverride(),
        showAllDiscounts: {
            visible: isQnB ? false : (!showDiscounts && _.get(quote, 'discounts_ML.value', []).length > 3)
        },
        hideAllDiscounts: {
            visible: isQnB ? false : (showDiscounts && _.get(quote, 'discounts_ML.value', []).length > 3)
        },
        effectiveDateSpan: { content: getFormattedDateString(_.get(quote, 'periodStartDate.value', undefined)) },
        effectiveDateDiv: {
            visible: !dateEditable
         },
        effectiveDateLiveRegion: {
            content: isQnB ? effDateLiveRegionMsg : ''
        },
        effectiveDateLink: {
            role: isQnB ? 'link' : 'button',
            'aria-label': isQnB ? `Effective Date ${getFormattedDateString(_.get(quote, 'periodStartDate.value', undefined))}` : ''
        },
        effectiveDatePicker: {
            visible: dateEditable,
            onValueChange: (value) => {
                if(!value) return; 

                if(isCompleteDate(value)) {
                    clearBlockAlerts('effectiveDateError');
                    if(!validateEffectiveDate(value)) {
                        addErrorAlert(messages.effectiveDateValidation, 'effectiveDateError');
                    } else {
                        onUpdateDate(value);
                    }
                }
            },
            disabled: isReadOnly,
            className: isQnB && fromParent,
            minDate: isQnB ? getCurrentDateObj() : {},
            maxDate: isQnB ? getMaxDateObj() : {},
            hideLabel: !isQnB,
            label: isQnB ? translator(messages.effectiveDate) : ''
        },
        bundleP: { visible: isBundle },
        mainButton: {
            content: mainButtonName,
            disabled: mainButtonName === translator(messages.requoting) || isReadOnly,
            className: isQnB ? "vertical-spacing-15 primaryButton" : "vertical-spacing-15 mobileButton"
        },
        callPhone: {
            visible: !isBundle,
            content: <FormattedMessage
                {...messages.callLabel}
                values={{
                    phoneNum: isQnB ? (<a href={`tel:+${translator(appConfig.gcsPhoneNumber)}`} style={{textDecoration: 'none'}}>{translator(appConfig.gcsPhoneNumber)}</a>) : (<strong>{appConfig.gcsPhoneNumber}</strong>)
                }}
            />,
            className: isQnB ? "body-copy vertical-spacing-30 callPhoneLinkQnB" : "body-copy vertical-spacing-30"
        },
        throbberLoading: {
            visible: mainButtonName === translator(messages.requoting) || showLoader()
        },
        purchasePolicy: {
            visible: !isBundle,
            className: isQnB ? "pTerm" : "body-copy font-weight-bold vertical-spacing-15"
        },
        purchaseDiv: {
            className: isQnB ? "purchase focus-outline" : "purchase"
        },
        emailPrintContainer: {
            visible: !isBundle
        },
        emailQuote: {
            visible: !isReadOnly && !isQnB,
            disabled: isQnB ? !!(quote?.value?.quoteStatus?.isStale) : !!(quote?.value?.quoteStatus?.isStale || quote?.value?.showLoading || isQuotingInProgress),
            className: quote?.value?.quoteStatus?.isStale || quote?.value?.showLoading || isQuotingInProgress ? styles.noPointerEvent : ''
        },
        emailPrintDivider: {
            visible: !isQnB
        },
        printQuote: {
            disabled:  isQnB ? !!(quote?.value?.quoteStatus?.isStale) : !!(quote?.value?.quoteStatus?.isStale || quote?.value?.showLoading || isQuotingInProgress),
            className: quote?.value?.quoteStatus?.isStale || quote?.value?.showLoading || isQuotingInProgress ? styles.noPointerEvent : ''
        },
        pleaseNoteContainer: {
            visible: isQnB
        },
        purchaseBox: {
            className: (isQnB && quote?.isCustom?.value) ? 'purchase-box vertical-spacing-20 purchaseBoxStyle' : 'purchase-box vertical-spacing-20'
        },
        privacyPolicyComponent:{
            hideHyperlink
        }
    };

    const resolvers = {
        resolveComponentMap: {
            changeplantooltipcomponent: ChangePlanTooltipComponent,
            privacyPolicyComponent: PrivacyPolicyComponent

        },
        resolveCallbackMap: {
            onToggleShowDiscounts: onToggleShowDiscounts,
            onToggleDateEditable: onToggleDateEditable,
            onEmail: onEmail,
            onPrint: onPrint,
            onRunMainButton: runMainButton
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={quote}
            classNameMap={styles}
            overrideProps={overrides}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

QuoteSummaryBarComponent.propTypes = {
    sessionid: PropTypes.string,
    submissionid: PropTypes.string,
    quote: PropTypes.shape({
        value: PropTypes.shape({}),
        premium: PropTypes.shape({
            value: PropTypes.shape({})
        })
    }).isRequired,
    recurringPlanPeriod: PropTypes.shape({
        premium: PropTypes.shape({})
    }),
    productCode: PropTypes.string,
    isCustomQuote: PropTypes.bool,
    autoTermTypeList: PropTypes.arrayOf(PropTypes.shape({})),
    getFullPayPlan: PropTypes.func,
    runMainButton: PropTypes.func,
    isQuotingInProgress: PropTypes.func,
    mainButtonName: PropTypes.string,
    emailQuote: PropTypes.func,
    isBundle: PropTypes.bool,
    updateCustomQuote: PropTypes.func,
    showDiscounts: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    onUpdateTerm: PropTypes.func.isRequired,
    onPrint: PropTypes.func,
    onEmail: PropTypes.func,
    updateEffDate: PropTypes.func
};

QuoteSummaryBarComponent.defaultProps = {
    isBundle: false,
    sessionid: undefined,
    submissionid: undefined,
    recurringPlanPeriod: undefined,
    productCode: undefined,
    autoTermTypeList: [],
    isCustomQuote: false,
    getFullPayPlan: _.noop,
    runMainButton: _.noop,
    isQuotingInProgress: _.noop,
    mainButtonName: undefined,
    emailQuote: _.noop,
    updateCustomQuote: _.noop,
    showDiscounts: false,
    isReadOnly: false,
    onEmail: _.noop,
    onPrint: _.noop,
    updateEffDate: _.noop
};

export default QuoteSummaryBarComponent;
